// Kit compartido del módulo Nómina (esquema `nomina`).
// Pantallas a medida → PostgREST: tablas por /rest/nomina.<tabla> (RLS) y
// RPCs nom_* con headers de profile `nomina`.
import { ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { makeRest } from '@/features/admin/shared';
import { fieldCls } from '@/features/inventario/shared';

export { Modal, Campo, Switch, fieldCls } from '@/features/inventario/shared';
export { makeRest } from '@/features/admin/shared';
export { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';

export const nom = makeRest('nomina');

const NOM = { headers: { 'Content-Profile': 'nomina', 'Accept-Profile': 'nomina' } };
export async function nomRpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  return (await apiClient.post<T>(`/rpc/${fn}`, args, NOM)).data;
}

export const money = (v: number | string | null | undefined) =>
  v == null ? '—' : `$ ${Number(v).toFixed(2)}`;

export const fecha = (v: string | null | undefined) => (v ? v.slice(0, 10) : '—');

// Fecha local de hoy YYYY-MM-DD (Ecuador UTC-5): evita el +1 día de toISOString() por la noche.
export const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Etiquetas de los enums de BD (Código del Trabajo)
export const TIPOS_CONTRATO: Record<string, string> = {
  indefinido: 'Indefinido (Art. 14)',
  eventual: 'Eventual ≤180 días (Art. 17)',
  ocasional: 'Ocasional ≤30 días (Art. 17)',
  temporada: 'De temporada (Art. 17)',
  obra_cierta: 'Por obra cierta (Art. 16)',
  obra_servicio: 'Obra o servicio del giro (Art. 16.1)',
  tarea: 'Por tarea (Art. 16)',
  destajo: 'A destajo (Art. 16)',
  aprendizaje: 'Aprendizaje',
};
export const FORMAS_BENEFICIO: Record<string, string> = {
  acumulado: 'Acumulado',
  mensualizado: 'Mensualizado',
};
export const GENEROS = ['masculino', 'femenino', 'otro'];
export const ESTADOS_CIVILES = ['soltero', 'casado', 'union_de_hecho', 'divorciado', 'viudo'];
export const BANCOS = ['Banco Pichincha', 'Banco de Guayaquil', 'Banco del Pacífico', 'Produbanco', 'Banco Bolivariano', 'Banco del Austro', 'Banco Internacional', 'Cooperativa JEP'];

export interface Departamento {
  id: string; codigo: string | null; nombre: string; descripcion: string | null; es_activo: boolean;
  etapa_productiva: string | null;
}
export const ETAPAS_PRODUCTIVAS: Record<string, string> = {
  campo: 'Campo (labores agrícolas)', cosecha: 'Cosecha', poscosecha: 'Poscosecha y empaque',
  indirecta: 'Producción indirecta', administracion: 'Administración',
};
export interface Cargo { id: string; codigo: string | null; nombre: string; descripcion: string | null; sueldo_referencial: number | null; es_activo: boolean }

export interface EmpleadoRow {
  id: string; codigo: string | null; partner_id: string; razon_social: string;
  identificacion: string; email: string | null; telefono: string | null;
  es_activo: boolean; cargas_familiares: number; discapacidad: boolean; frecuencia_pago: string;
  contrato_id: string | null; tipo_contrato: string | null; fecha_inicio: string | null;
  sueldo: number | null; cargo: string | null; departamento: string | null;
  centro_costo_id: string | null; centro_costo: string | null;
  jornada?: string | null;
}

// Fincas / centros de costo (contabilidad.centros_costo) — mismo RPC y headers que
// Financiero/POS/Activo Fijo (features/financiero/centroCosto.ts, features/pos/shared.tsx).
export interface CentroCostoOpt { id: string; codigo: string | null; nombre: string }
export const centrosCostoList = async (): Promise<CentroCostoOpt[]> =>
  (await apiClient.post<CentroCostoOpt[]>('/rpc/cont_centros', {}, { headers: { 'Content-Profile': 'contabilidad', 'Accept-Profile': 'contabilidad' } })).data || [];

export const FRECUENCIAS_PAGO: Record<string, string> = {
  mensual: 'Mensual', quincenal: 'Quincenal (anticipo el 15)', semanal: 'Semanal (anticipo viernes)',
  bisemanal: 'Bisemanal (cada 2 semanas)',
};

export interface EmpleadoFicha {
  id: string; codigo: string | null; partner_id: string;
  razon_social: string; identificacion: string; tipo_identificacion: string | null;
  email: string | null; telefono: string | null; celular: string | null;
  direccion: string | null; ciudad: string | null; provincia: string | null; nacionalidad: string | null;
  fecha_nacimiento: string | null; genero: string | null; estado_civil: string | null;
  cargas_familiares: number; discapacidad: boolean; discapacidad_pct: number | null;
  contacto_emergencia_nombre: string | null; contacto_emergencia_telefono: string | null;
  banco_nombre: string | null; banco_codigo: string | null; banco_tipo_cuenta: string | null; banco_cuenta: string | null;
  frecuencia_pago: string; es_activo: boolean;
  centro_costo_id: string | null; centro_costo_nombre: string | null;
}

export interface CargaFamiliar {
  id: string; parentesco: 'conyuge' | 'conviviente' | 'hijo' | 'padre' | 'madre'; nombre: string;
  identificacion: string | null; fecha_nacimiento: string | null;
  discapacidad: boolean; acreditada: boolean; documento: string | null;
  percibe_ingresos: boolean; califica_utilidades: boolean; califica_ir: boolean;
}
export interface CargasData { validas_utilidades: number; validas_ir: number; cargas: CargaFamiliar[] }
export const PARENTESCOS: Record<string, string> = {
  conyuge: 'Cónyuge', conviviente: 'Conviviente (unión de hecho)', hijo: 'Hijo/a',
  padre: 'Padre', madre: 'Madre',
};

export const TIPOS_LICENCIA: Record<string, { label: string; tope: number; nota: string }> = {
  maternidad: { label: 'Maternidad', tope: 94, nota: '12 semanas (+10 por parto múltiple); compartible con el padre hasta el 75%. Empleador paga el 25% (IESS subsidia el resto).' },
  paternidad: { label: 'Paternidad', tope: 25, nota: '10 días (+5 cesárea/múltiple; 18 prematuro; 25 enfermedad grave). Pagada 100%.' },
  adopcion: { label: 'Adopción', tope: 15, nota: '15 días desde la entrega legal. Pagada 100%.' },
  hijo_hospitalizado: { label: 'Hijo hospitalizado', tope: 25, nota: '25 días, madre o padre, conjunta o alternada. Pagada 100%.' },
  no_remunerada: { label: 'Cuidado de hijos (sin sueldo)', tope: 270, nota: 'Hasta 9 meses tras maternidad/paternidad; cuenta para antigüedad. Sin remuneración.' },
  enfermedad_embarazo: { label: 'Enfermedad por embarazo/parto', tope: 365, nota: 'Art. 154: conserva el puesto hasta 1 año; sin remuneración tras las 12 semanas.' },
  lactancia: { label: 'Lactancia (jornada 6h)', tope: 366, nota: '12 meses post-parto: jornada de 6 horas con remuneración completa. NO es ausencia.' },
};
export interface LicenciaRow {
  id: string; empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  tipo: string; desde: string; hasta: string; dias: number; pct_empleador: number;
  certificado: string | null; observacion: string | null; vigente: boolean;
}
export interface PortalLicencia { tipo: string; desde: string; hasta: string; dias: number; pct_empleador: number; vigente: boolean }

export const TIPOS_ARCO: Record<string, string> = {
  acceso: 'Acceso a mis datos', rectificacion: 'Rectificación', eliminacion: 'Eliminación',
  oposicion: 'Oposición', portabilidad: 'Portabilidad', suspension: 'Suspensión del tratamiento',
};
export const ESTADOS_ARCO: Record<string, string> = {
  pendiente: 'Pendiente', en_proceso: 'En proceso', atendida: 'Atendida', rechazada: 'Rechazada',
};
export interface ArcoRow {
  id: string; tipo: string; estado: string; descripcion: string; fecha_limite: string;
  respuesta: string | null; created_at: string; resuelta_at: string | null;
  empleado_codigo: string | null; razon_social: string; identificacion: string; vencida: boolean;
}
export interface PortalArco {
  id: string; tipo: string; estado: string; descripcion: string; fecha_limite: string;
  respuesta: string | null; created_at: string; resuelta_at: string | null;
}

export interface AjusteRow {
  id: string; empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  tipo: 'ingreso' | 'egreso'; gravable: boolean; monto: number; periodo_afectado: string | null;
  motivo: string; estado: string; rol_id: string | null; rol_codigo: string | null; created_at: string;
}

export interface PortalAnticipo {
  id: string; fecha: string; monto: number; motivo: string | null;
  origen: 'manual' | 'quincena' | 'semana'; periodo: string | null; estado: string;
}

export interface FeriadoRow {
  id: string; fecha: string; nombre: string; ambito: 'nacional' | 'local'; observacion: string | null;
}

export const TIPOS_NOVEDAD: Record<string, { label: string; unidad: 'horas' | 'dias' }> = {
  hora_suplementaria: { label: 'Horas suplementarias 50% (Art. 55)', unidad: 'horas' },
  hora_suplementaria_noct: { label: 'Horas suplementarias nocturnas 100% (Art. 55.2)', unidad: 'horas' },
  hora_extraordinaria: { label: 'Horas extraordinarias 100% (Art. 55)', unidad: 'horas' },
  recargo_nocturno: { label: 'Recargo nocturno 25% (Art. 49)', unidad: 'horas' },
  falta: { label: 'Falta injustificada (Art. 54)', unidad: 'dias' },
  permiso: { label: 'Permiso remunerado', unidad: 'dias' },
  permiso_no_remunerado: { label: 'Permiso no remunerado', unidad: 'dias' },
  subsidio: { label: 'Subsidio (enfermedad/maternidad)', unidad: 'dias' },
};

export interface TramoIR { desde: number; hasta: number | null; imp_basico: number; pct_exceso: number }
export interface Parametros {
  id: string; anio: number; sbu: number;
  aporte_personal_pct: number; aporte_patronal_pct: number;
  iece_pct: number; secap_pct: number; fondos_reserva_pct: number;
  region_decimo_cuarto: 'costa' | 'sierra';
  canasta_basica: number | null;
  salario_digno: number | null;
  subsidio_enfermedad_pct: number;
  vacaciones_modo: 'neto' | 'provision';
  base_calculo: 'mensual' | 'jornal';
  dias_divisor: number;
  dias_semana_default: number;
  incluir_horas_extra: boolean;
  jornal_recargo_pct: number;
  codigo_empleado_prefijo: string;
  codigo_empleado_digitos: number;
  tabla_ir: TramoIR[];
}
export interface Rubro {
  id: string; codigo: string; nombre: string; tipo: 'ingreso' | 'egreso';
  afecta_iess: boolean; afecta_ir: boolean; es_sistema: boolean; es_activo: boolean;
}
export interface NovedadRow {
  id: string; empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  fecha: string; tipo: string; horas: number | null; dias: number | null; motivo: string | null; rol_id: string | null;
}
export interface AnticipoRow {
  id: string; empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  fecha: string; monto: number; motivo: string | null; estado: string;
  origen: 'manual' | 'quincena' | 'semana'; periodo: string | null;
}
export interface PrestamoAbono { id: string; fecha: string; monto: number; origen: string; nota: string | null }
export interface PrestamoRow {
  id: string; codigo: string | null; empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  fecha: string; monto: number; cuotas: number; cuota_mensual: number; saldo: number;
  motivo: string | null; estado: string; abonos: PrestamoAbono[];
}

export const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export const TIPOS_ROL: Record<string, string> = {
  general: 'General (todos)', mensual: 'Mensual', quincenal: 'Quincenal', semanal: 'Semanal (anticipo)',
  semanal_firme: 'Semanal en firme', bisemanal_firme: 'Bisemanal en firme',
};

// Muestra SOLO los tipos de rol que la empresa realmente usa (derivado de sus
// roles vía nom_tipos_rol → parametrizable por empresa). Si aún no tiene roles,
// cae a todos los tipos para no quedar vacío.
export function TipoRolSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data: usados } = useQuery<string[]>({ queryKey: ['nom-tipos-rol'], queryFn: () => nomRpc<string[]>('nom_tipos_rol') });
  const claves = usados && usados.length ? usados : Object.keys(TIPOS_ROL);
  return (
    <select className={`${fieldCls} w-44`} value={value} onChange={(e) => onChange(e.target.value)} aria-label="Tipo de rol">
      <option value="">Todos los tipos de rol</option>
      {claves.map((k) => <option key={k} value={k}>{TIPOS_ROL[k] ?? k}</option>)}
    </select>
  );
}
export interface RolHeader {
  id: string; codigo: string | null; anio: number; mes: number;
  tipo: 'general' | 'mensual' | 'quincenal' | 'semanal';
  estado: 'borrador' | 'cerrado' | 'pagado'; n_empleados: number;
  total_ingresos: number; total_egresos: number; total_neto: number;
  fecha_generado: string | null; fecha_cerrado: string | null; fecha_pagado: string | null;
}
export interface RolRubro { rubro_codigo: string; descripcion: string; tipo: 'ingreso' | 'egreso'; cantidad: number | null; valor: number; orden: number }
export interface RolEmpleadoRow {
  id: string; empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  cargo: string | null; departamento: string | null; sueldo_base: number; dias: number;
  ingresos: number; egresos: number; neto: number; rubros: RolRubro[];
}
export interface RolDetalleData {
  rol: RolHeader | null;
  asiento?: { id: string; numero: number; fecha: string } | null;
  empleados: RolEmpleadoRow[];
}
export const DIAS_SEMANA = ['L', 'M', 'X', 'J', 'V', 'S', 'D']; // ISO 1..7
export interface Turno {
  id: string; codigo: string | null; nombre: string;
  hora_entrada: string; hora_salida: string; receso_min: number; tolerancia_min: number;
  dias: number[]; es_activo: boolean;
}
export interface AsignacionTurno {
  empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  turno_id: string | null; turno: string | null; hora_entrada: string | null; hora_salida: string | null;
  dias: number[] | null; desde: string | null;
}
export interface MarcacionRow {
  id: string; empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  fecha: string; entrada_at: string; salida_at: string | null; origen: string;
  atraso_min: number | null; horas: number | null; extra_no_generada: number | null;
  procesada_at: string | null; observacion: string | null;
  turno: string | null; hora_entrada: string | null; hora_salida: string | null; laborable: boolean | null;
}
export interface PortalAsistenciaData {
  turno: { nombre: string; hora_entrada: string; hora_salida: string; dias: number[] } | null;
  abierta: { fecha: string; entrada: string } | null;
  marcaciones: { fecha: string; entrada: string; salida: string | null; horas: number | null; atraso_min: number | null }[];
}

export interface EnvioRol { estado: 'pendiente' | 'enviando' | 'enviado' | 'error'; email: string; intentos: number; error: string | null; enviado_at: string | null }
export interface RolEnviosData {
  resumen: { total: number; con_email: number; sin_email: number; enviados: number; pendientes: number; errores: number };
  envios: Record<string, EnvioRol>;
}
export interface SmtpConfigData {
  host: string | null; puerto: number | null; seguro: boolean | null; usuario: string | null;
  from_email: string | null; from_nombre: string | null; activo: boolean; password_configurada: boolean;
}

export interface Acreditacion {
  neto: number; empleado_codigo: string | null; razon_social: string; identificacion: string;
  banco_nombre: string | null; banco_tipo_cuenta: string | null; banco_cuenta: string | null;
}

// ── Pago por transferencia (N-09) ────────────────────────────────────────────
// El layout del archivo del banco es DATO: cada banco pide lo suyo y cambia
// anchos sin avisar, así que se describe campo por campo y se edita en pantalla.
export interface CampoFormato {
  campo?: string | null; titulo?: string; ancho?: number | null; relleno?: string;
  alinea?: 'izq' | 'der'; formato?: string | null; mapa?: Record<string, string>; valor?: string;
}
export interface PagoFormato {
  id: string; nombre: string; banco: string | null; tipo: 'csv' | 'fijo';
  separador: string; encabezado: boolean; extension: string; fin_linea: 'lf' | 'crlf';
  campos: CampoFormato[]; es_sistema: boolean; activo: boolean;
}
export interface PagoValidacion {
  total: number; listos: number; incompletos: number;
  monto_listo: number; monto_trabado: number;
  detalle: { empleado: string; codigo: string | null; neto: number; falta: string }[];
}
export interface ArchivoBanco {
  formato: string; archivo: string; contenido: string; lineas: number; total: number;
  omitidos: { empleado: string; codigo: string | null; neto: number; falta: string }[];
}
// Los campos que el renderizador sabe resolver (ver comentario de
// nomina.pago_formatos.campos en el script 800).
export const CAMPOS_ARCHIVO: { valor: string; label: string }[] = [
  { valor: 'identificacion', label: 'Identificación' },
  { valor: 'tipo_identificacion', label: 'Tipo de identificación (C/R/P)' },
  { valor: 'nombre', label: 'Nombre del beneficiario' },
  { valor: 'banco_codigo', label: 'Código del banco' },
  { valor: 'banco_nombre', label: 'Nombre del banco' },
  { valor: 'tipo_cuenta', label: 'Tipo de cuenta' },
  { valor: 'cuenta', label: 'Número de cuenta' },
  { valor: 'monto', label: 'Monto (0.00)' },
  { valor: 'monto_centavos', label: 'Monto en centavos (sin punto)' },
  { valor: 'codigo_empleado', label: 'Código del empleado' },
  { valor: 'secuencia', label: 'Número de línea' },
  { valor: 'fecha', label: 'Fecha de pago' },
  { valor: 'referencia', label: 'Código del rol' },
  { valor: 'email', label: 'Correo' },
  { valor: '', label: '— Constante —' },
];

// ── Revisión anual del salario básico (M-03) ─────────────────────────────────
export interface SbuRevision {
  id: string; anio: number; sbu: number; estado: 'borrador' | 'aplicada' | 'anulada';
  n_contratos: number; costo_anual: number; motivo: string | null;
  hash_acta: string | null; aplicada_at: string | null; created_at: string;
  aplicada_por?: string | null;
}
export interface SbuRevisionLinea {
  id: string; contrato_id: string; empleado_id: string; sueldo_anterior: number;
  minimo: number; sueldo_nuevo: number; aplicar: boolean; nota: string | null;
  aumento: number; empleado_codigo: string | null; empleado: string;
  identificacion: string | null; contrato: string | null; horas_semanales: number;
  cargo: string | null;
}

// ── Carga masiva de personal (M-04) ──────────────────────────────────────────
export interface ImportCabecera {
  id: string; archivo: string | null; estado: 'borrador' | 'aplicada' | 'anulada';
  n_filas: number; n_ok: number; n_aviso: number; n_error: number;
  n_aplicadas: number; created_at: string; aplicada_at: string | null;
}
export interface ImportFila {
  id: string; linea: number; estado: 'ok' | 'aviso' | 'error' | 'aplicada' | 'fallida';
  errores: string[]; avisos: string[]; mensaje: string | null;
  identificacion: string | null; razon_social: string | null; sueldo: string | null;
  fecha_inicio: string | null; tipo_contrato: string | null; cargo: string | null;
}
export interface ColumnaPlantilla { col: string; titulo: string; req: boolean }

// ── Anexos SRI / IESS (M-06) ─────────────────────────────────────────────────
export interface AnexoValidacion {
  anexo: string; anio: number; mes: number | null; personas: number;
  graves: number; avisos: number;
  problemas: { quien: string; problema: string; grave: boolean }[];
}
export interface AnexoArchivo {
  formato: string; archivo: string; contenido: string; lineas: number;
  validacion: AnexoValidacion;
}

// ── Simulador de costo (M-08) ────────────────────────────────────────────────
export interface CostoEscenario {
  nombre: string; anio: number; meses: number; sueldo: number; horas_semanales: number;
  sbu: number | null; sueldos: number; decimo_tercero: number; decimo_cuarto: number;
  fondos_reserva: number; meses_con_fr: number; vacaciones: number;
  aporte_patronal: number; iece: number; secap: number;
  costo_anual: number; costo_mensual: number; costo_hora: number;
  aporte_personal: number; ir_retenido: number; neto_anual: number; neto_mensual: number;
  por_dolar_neto: number; sobre_sueldo_pct: number;
}
export interface CostoSimulacion {
  escenarios: CostoEscenario[]; mas_barato: string | null; diferencia: number;
}

export interface SbuDivergencia {
  anio: number; sbu_empresa: number; sbu_legal: number; pais: string;
  diferencia: number; estado: 'bajo_el_minimo' | 'sobre_el_minimo' | 'alineado'; fuente: string | null;
}
export interface ContratoBajoSbu {
  contrato_id: string; codigo: string | null; fecha_inicio: string; jornada: string;
  horas_semanales: number; empleado_codigo: string | null; empleado: string;
  identificacion: string | null; sueldo: number; anio_ref: number; sbu: number;
  minimo: number; faltante: number; costo_anual: number;
}

export interface BeneficioHeader {
  id: string; codigo: string | null; tipo: 'decimo_tercero' | 'decimo_cuarto' | 'utilidades';
  periodo: number; estado: 'borrador' | 'cerrado' | 'pagado'; monto_utilidad: number | null;
  n_empleados: number; total: number; fecha_pagado: string | null;
}
export interface BeneficioDetalle {
  id: string; empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  base: number | null; dias: number | null; cargas: number | null; ya_pagado: number;
  valor: number; valor_cargas: number; total: number;
}
export interface AsientoRef { id: string; numero: number; fecha: string }
export interface BeneficioData { beneficio: BeneficioHeader | null; asiento?: AsientoRef | null; detalles: BeneficioDetalle[] }
export interface FondoReservaRow {
  empleado_codigo: string | null; razon_social: string; identificacion: string;
  fecha_ingreso: string | null; con_derecho: boolean; modalidad: string;
  base: number; pagado_rol: number; fondo: number;
}
export interface FondosReservaData { rol: { id: string; estado: string; codigo: string | null } | null; empleados: FondoReservaRow[] }
export interface VacacionSaldo {
  empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  es_activo: boolean; fecha_ingreso: string; anios_completos: number;
  dias_ganados: number; dias_gozados: number; saldo: number;
}
export interface VacacionGoce {
  id: string; desde: string; hasta: string; dias: number; observacion: string | null;
  liquidacion_id: string | null; liq_valor: number | null; asiento_numero: number | null;
}
export interface VacacionProvision { acumulada: number; liquidada: number; disponible: number }

export const CAUSAS_SALIDA: Record<string, string> = {
  desahucio: 'Desahucio / renuncia (Art. 184)',
  acuerdo_partes: 'Acuerdo entre las partes (Art. 169.2)',
  despido_intempestivo: 'Despido intempestivo (Art. 188)',
  visto_bueno_trabajador: 'Visto bueno pedido por el trabajador (Art. 173)',
  visto_bueno_empleador: 'Visto bueno pedido por el empleador (Art. 172)',
  plazo_vencido: 'Conclusión de obra o plazo (Art. 169.3)',
  muerte: 'Muerte del trabajador (Art. 169.4)',
};

export interface LiquidacionRow {
  id: string; codigo: string | null; empleado_id: string; contrato_id: string;
  empleado_codigo: string | null; razon_social: string; identificacion: string;
  causa: string; fecha_salida: string; anios_servicio: number; ultima_remuneracion: number;
  estado: 'borrador' | 'cerrado' | 'pagado';
  total_ingresos: number; total_egresos: number; total_neto: number;
  fecha_pagado: string | null; observaciones: string | null;
}
export interface LiquidacionDetalleLinea { concepto: string; descripcion: string; tipo: 'ingreso' | 'egreso'; valor: number; orden: number }
export interface LiquidacionData {
  liquidacion: (LiquidacionRow & { tipo_contrato: string; fecha_inicio: string; cargo: string | null }) | null;
  avisos?: string[] | null;
  asiento?: AsientoRef | null;
  detalles: LiquidacionDetalleLinea[];
}

export interface PlanillaIessRow {
  empleado_codigo: string | null; razon_social: string; identificacion: string;
  // Una línea por AFILIADO: `periodos` dice cuántos roles del mes se
  // consolidaron en ella (1 en nómina mensual, 4 o 5 en semanal).
  periodos: number; dias: number;
  base: number; ap_personal: number; ap_patronal: number; iece: number; secap: number; total: number;
}
export interface PlanillaIessData {
  rol: { id: string; estado: string; codigo: string | null } | null;
  porcentajes: { personal: number; patronal: number; iece: number; secap: number } | null;
  empleados: PlanillaIessRow[];
}
export interface PrestamoIessRow {
  id: string; empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  tipo: 'quirografario' | 'hipotecario'; cuota_mensual: number; fecha_inicio: string; estado: string; observacion: string | null;
}
export interface IrProyeccionRow {
  empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  id: string | null; gastos_personales: number | null; rebaja_manual: number | null;
  cargas_ir: number; limite: number | null; rebaja_aplicada: number;
}
export interface Form107Row {
  empleado_codigo: string | null; razon_social: string; identificacion: string;
  ingresos_gravados: number; ingresos_exentos: number; aporte_iess: number; rebaja_gastos: number; ir_retenido: number;
}
export interface RepRubroRow { rubro_codigo: string; descripcion: string; tipo: 'ingreso' | 'egreso' | 'provision'; n: number; total: number }
export interface RepDeptoRow { departamento: string; n_empleados: number; ingresos: number; egresos: number; neto: number }
export interface RepRolGeneralData {
  rol: { id: string; estado: string; codigo: string | null; n_empleados: number; total_ingresos: number; total_egresos: number; total_neto: number } | null;
  por_rubro: RepRubroRow[]; por_departamento: RepDeptoRow[];
}
export interface RepProvisionRow {
  empleado_codigo: string | null; razon_social: string; identificacion: string; departamento: string;
  base: number; prov_d13: number; prov_d14: number; prov_vacaciones: number; prov_fr: number; ap_patronal: number; total: number;
}
export interface RepCostoRow {
  departamento: string; n_empleados: number; remuneraciones: number; provisiones: number; ap_patronal: number; costo_total: number;
}

// --- Portal del Empleado ---
export interface PortalPerfil {
  empleado: {
    codigo: string | null; razon_social: string; identificacion: string;
    fecha_nacimiento: string | null; genero: string | null; estado_civil: string | null;
    cargas_familiares: number; email: string | null; celular: string | null; telefono: string | null;
    direccion: string | null; ciudad: string | null;
    banco_nombre: string | null; banco_tipo_cuenta: string | null; banco_cuenta: string | null;
    contacto_emergencia_nombre: string | null; contacto_emergencia_telefono: string | null;
  } | null;
  contrato: {
    tipo: string; fecha_inicio: string; jornada: string; horas_semanales: number;
    modalidad: string; sueldo: number; estado: string; cargo: string | null; departamento: string | null;
  } | null;
}
export interface PortalRol {
  id: string; anio: number; mes: number; estado: string; fecha_pagado: string | null;
  cargo: string | null; departamento: string | null; dias: number; sueldo_base: number;
  ingresos: number; egresos: number; neto: number; rubros: RolRubro[];
}
export interface PortalVacacionesData {
  saldo: { fecha_ingreso: string; anios_completos: number; dias_ganados: number; dias_gozados: number; saldo: number };
  goces: { desde: string; hasta: string; dias: number; observacion: string | null }[];
}
export interface SolicitudRow {
  id: string; tipo: 'vacaciones' | 'anticipo' | 'permiso';
  datos: Record<string, string | number | boolean | null>;
  estado: 'pendiente' | 'aprobada' | 'rechazada'; respuesta: string | null;
  created_at: string; resuelto_at: string | null;
  empleado_codigo?: string | null; razon_social?: string; identificacion?: string;
}
export interface PortalCertificado {
  empresa: { nombre: string; ruc: string | null; direccion: string | null } | null;
  empleado: { razon_social: string; identificacion: string } | null;
  contrato: { tipo: string; fecha_inicio: string; jornada: string; horas_semanales: number; sueldo: number; estado: string; cargo: string | null } | null;
}
export const TIPOS_SOLICITUD: Record<string, string> = {
  vacaciones: 'Vacaciones', anticipo: 'Anticipo de sueldo', permiso: 'Permiso',
};

export interface ContratoRow {
  id: string; codigo: string | null; empleado_id: string; empleado_codigo: string | null;
  razon_social: string; identificacion: string;
  tipo: string; fecha_inicio: string; fecha_fin: string | null; periodo_prueba_dias: number;
  jornada: string; horas_semanales: number; modalidad: string;
  departamento_id: string | null; cargo_id: string | null; cargo: string | null; departamento: string | null;
  sueldo: number; decimo_tercero: string; decimo_cuarto: string; fondos_reserva: string;
  estado: string; fecha_terminacion: string | null; motivo_terminacion: string | null; observaciones: string | null;
  pdf_estado: 'pendiente' | 'generando' | 'firmado' | 'error' | null;
  pdf_firmado_at: string | null; pdf_error: string | null;
}

export function descargarPdfBase64(b64: string, filename: string) {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ---- Ordenamiento de tablas (asc/desc por columna) — mismo patrón que
// features/control-bancario/shared.tsx: clic en el encabezado alterna asc/desc.
export type SortDir = 'asc' | 'desc';
export interface SortState { key: string; dir: SortDir; toggle: (k: string) => void }
export function useSort<T>(rows: T[], accessor: (row: T, key: string) => string | number | null,
                           initialKey = '', initialDir: SortDir = 'asc') {
  const [key, setKey] = useState(initialKey);
  const [dir, setDir] = useState<SortDir>(initialDir);
  const toggle = (k: string) => { if (k === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc')); else { setKey(k); setDir('asc'); } };
  const sorted = useMemo(() => {
    if (!key) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = accessor(a, key); const vb = accessor(b, key);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;   // nulos al final
      if (vb == null) return -1;
      const c = (typeof va === 'number' && typeof vb === 'number')
        ? va - vb
        : String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' });
      return dir === 'asc' ? c : -c;
    });
    return arr;
  }, [rows, key, dir, accessor]);
  return { sorted, key, dir, toggle } as { sorted: T[] } & SortState;
}

export function SortTh({ children, col, sort, align = 'left', className = '', tip }:
  { children: ReactNode; col: string; sort: SortState; align?: 'left' | 'right' | 'center'; className?: string; tip?: string }) {
  const active = sort.key === col;
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={`py-2 pr-3 ${alignCls} cursor-pointer select-none hover:text-foreground ${className}`}
        onClick={() => sort.toggle(col)} title="Ordenar">
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {children}
        {tip && (
          <span title={tip} onClick={(e) => e.stopPropagation()}
                className="cursor-help text-[9px] leading-none rounded-full border border-current/40 text-muted-foreground/70 w-3.5 h-3.5 inline-flex items-center justify-center font-serif">i</span>
        )}
        <span className={`text-[10px] leading-none ${active ? 'text-foreground' : 'text-muted-foreground/40'}`}>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </th>
  );
}
